import React, { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import toast from "react-hot-toast";
import {
  CreditCard, CheckCircle, Clock, XCircle, ChevronDown,
  User, Calendar, DollarSign, FileText, RefreshCw,
} from "lucide-react";

// ── Tipos ─────────────────────────────────────────────────────────────────
interface Concepto {
  key: string;
  label: string;
  precio_sugerido: number;
}

interface WompiConfig {
  referencia: string;
  amount_in_cents: number;
  integrity: string;
  public_key: string;
  currency: string;
  redirect_url: string;
  concepto_label: string;
}

interface PagoHistorial {
  id: string;
  concepto_label: string;
  concepto_detalle: string | null;
  valor: number;
  referencia: string;
  estado: string;
  wompi_transaction_id: string | null;
  fecha: string | null;
  notas: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────
const COP = (v: number) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(v);

const HOY = new Date().toLocaleDateString("es-CO", {
  weekday: "long", year: "numeric", month: "long", day: "numeric",
});

const ESTADO_BADGE: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  PENDING:  { label: "Pendiente", cls: "bg-yellow-100 text-yellow-700", icon: <Clock size={13}/> },
  APPROVED: { label: "Aprobado",  cls: "bg-green-100 text-green-700",  icon: <CheckCircle size={13}/> },
  DECLINED: { label: "Declinado", cls: "bg-red-100 text-red-700",      icon: <XCircle size={13}/> },
  VOIDED:   { label: "Anulado",   cls: "bg-gray-100 text-gray-600",    icon: <XCircle size={13}/> },
  ERROR:    { label: "Error",     cls: "bg-red-100 text-red-700",      icon: <XCircle size={13}/> },
};

// ── Componente principal ──────────────────────────────────────────────────
export default function PagoOnlinePage() {
  const { user } = useAuth();

  // Formulario
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [concepto, setConcepto] = useState("");
  const [conceptoDetalle, setConceptoDetalle] = useState("");
  const [valor, setValor] = useState("");
  const [notas, setNotas] = useState("");
  const [loading, setLoading] = useState(false);

  // Estado Wompi
  const [wompiConfig, setWompiConfig] = useState<WompiConfig | null>(null);
  const [step, setStep] = useState<"form" | "checkout">("form");

  // Historial
  const [historial, setHistorial] = useState<PagoHistorial[]>([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const btnContainerRef = useRef<HTMLDivElement>(null);

  // Cargar conceptos y historial al montar
  useEffect(() => {
    api.get("/wompi/conceptos").then(r => setConceptos(r.data)).catch(() => {});
    cargarHistorial();
  }, []);

  // Cuando cambia el concepto, sugerir precio
  useEffect(() => {
    const c = conceptos.find(x => x.key === concepto);
    if (c && c.precio_sugerido > 0) setValor(String(c.precio_sugerido));
    else if (c) setValor("");
  }, [concepto, conceptos]);

  // Inyectar script Wompi cuando llega la config
  useEffect(() => {
    if (!wompiConfig || step !== "checkout") return;

    const container = btnContainerRef.current;
    if (!container) return;

    // Limpiar scripts previos
    container.innerHTML = "";

    const script = document.createElement("script");
    script.src = "https://checkout.wompi.co/widget.js";
    script.setAttribute("data-render", "button");
    script.setAttribute("data-public-key", wompiConfig.public_key);
    script.setAttribute("data-currency", wompiConfig.currency);
    script.setAttribute("data-amount-in-cents", String(wompiConfig.amount_in_cents));
    script.setAttribute("data-reference", wompiConfig.referencia);
    script.setAttribute("data-signature:integrity", wompiConfig.integrity);
    script.setAttribute("data-redirect-url", wompiConfig.redirect_url);
    // Datos del pagador
    if (user?.email) script.setAttribute("data-customer-data:email", user.email);
    const fullName = [user?.nombre, user?.apellido].filter(Boolean).join(" ");
    if (fullName) script.setAttribute("data-customer-data:full-name", fullName);

    container.appendChild(script);
  }, [wompiConfig, step]);

  async function cargarHistorial() {
    setLoadingHistorial(true);
    try {
      const r = await api.get("/wompi/mis-pagos");
      setHistorial(r.data);
    } catch {
      /* silencioso */
    } finally {
      setLoadingHistorial(false);
    }
  }

  async function handleCrearPago(e: React.FormEvent) {
    e.preventDefault();
    const valorNum = parseInt(valor.replace(/\D/g, ""), 10);
    if (!concepto) return toast.error("Seleccioná un concepto de pago");
    if (!valorNum || valorNum < 1000) return toast.error("El valor mínimo es $1.000 COP");

    setLoading(true);
    try {
      const r = await api.post("/wompi/crear", {
        concepto,
        concepto_detalle: conceptoDetalle || null,
        valor: valorNum,
        notas: notas || null,
      });
      setWompiConfig(r.data);
      setStep("checkout");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Error al preparar el pago");
    } finally {
      setLoading(false);
    }
  }

  function handleVolver() {
    setStep("form");
    setWompiConfig(null);
    if (btnContainerRef.current) btnContainerRef.current.innerHTML = "";
    cargarHistorial();
  }

  const valorNum = parseInt(valor.replace(/\D/g, ""), 10) || 0;
  const nombreCompleto = [user?.nombre, user?.apellido].filter(Boolean).join(" ");
  const concLabel = conceptos.find(x => x.key === concepto)?.label || "";

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-[#0D1B2A] flex items-center gap-2">
            <CreditCard size={26} className="text-blue-600" />
            Pago en Línea
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Realizá pagos de cuotas, uniformes, viajes y más directamente con Wompi.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ── Panel izquierdo: formulario / checkout ── */}
          <div className="lg:col-span-3">
            {step === "form" ? (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-base font-bold text-[#0D1B2A] mb-5 flex items-center gap-2">
                  <FileText size={17} className="text-blue-600" />
                  Datos del pago
                </h2>

                {/* Info automática */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                      <User size={12} /> Pagador
                    </div>
                    <div className="font-semibold text-sm text-[#0D1B2A] truncate">{nombreCompleto || "—"}</div>
                    <div className="text-xs text-gray-400 truncate">{user?.email}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                      <Calendar size={12} /> Fecha
                    </div>
                    <div className="font-semibold text-sm text-[#0D1B2A] capitalize">{HOY}</div>
                  </div>
                </div>

                <form onSubmit={handleCrearPago} className="space-y-4">
                  {/* Concepto */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Concepto de pago <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={concepto}
                        onChange={e => setConcepto(e.target.value)}
                        required
                        className="w-full appearance-none border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="">— Seleccioná un concepto —</option>
                        {conceptos.map(c => (
                          <option key={c.key} value={c.key}>
                            {c.label}{c.precio_sugerido > 0 ? `  ·  ${COP(c.precio_sugerido)}` : ""}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Detalle adicional */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Detalle / descripción{" "}
                      <span className="text-gray-400 font-normal">(opcional)</span>
                    </label>
                    <input
                      type="text"
                      value={conceptoDetalle}
                      onChange={e => setConceptoDetalle(e.target.value)}
                      placeholder="Ej: Mes de mayo 2026, Talla M…"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Valor */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Valor a pagar (COP) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="number"
                        value={valor}
                        onChange={e => setValor(e.target.value)}
                        required
                        min={1000}
                        max={50000000}
                        placeholder="150000"
                        className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {valorNum >= 1000 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Total: <strong className="text-[#0D1B2A]">{COP(valorNum)}</strong>
                      </p>
                    )}
                  </div>

                  {/* Notas */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Notas <span className="text-gray-400 font-normal">(opcional)</span>
                    </label>
                    <textarea
                      value={notas}
                      onChange={e => setNotas(e.target.value)}
                      rows={2}
                      placeholder="Información adicional para la academia…"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2 text-sm"
                  >
                    {loading ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" /> Preparando pago…
                      </>
                    ) : (
                      <>
                        <CreditCard size={16} /> Continuar al pago
                      </>
                    )}
                  </button>
                </form>
              </div>
            ) : (
              /* ── Checkout Wompi ── */
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-base font-bold text-[#0D1B2A] mb-1 flex items-center gap-2">
                  <CreditCard size={17} className="text-green-600" />
                  Confirmar y pagar
                </h2>
                <p className="text-xs text-gray-500 mb-5">
                  Revisá los datos y presioná el botón de Wompi para completar el pago de forma segura.
                </p>

                {/* Resumen */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Concepto</span>
                    <span className="font-semibold text-[#0D1B2A]">{wompiConfig?.concepto_label}</span>
                  </div>
                  {conceptoDetalle && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Detalle</span>
                      <span className="font-semibold text-[#0D1B2A]">{conceptoDetalle}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Pagador</span>
                    <span className="font-semibold text-[#0D1B2A]">{nombreCompleto}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Fecha</span>
                    <span className="font-semibold text-[#0D1B2A] capitalize">{HOY}</span>
                  </div>
                  <div className="border-t border-blue-100 pt-2 flex justify-between">
                    <span className="font-bold text-[#0D1B2A]">Total</span>
                    <span className="font-extrabold text-blue-700 text-lg">
                      {COP((wompiConfig?.amount_in_cents || 0) / 100)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Referencia: <code className="font-mono">{wompiConfig?.referencia}</code>
                  </div>
                </div>

                {/* Botón Wompi se inyecta aquí */}
                <div
                  ref={btnContainerRef}
                  id="wompi-button-container"
                  className="flex justify-center mb-4 min-h-[48px]"
                />

                <p className="text-center text-xs text-gray-400 mb-4">
                  🔒 Pago 100% seguro procesado por{" "}
                  <a
                    href="https://wompi.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    Wompi
                  </a>{" "}
                  · Acepta tarjetas de crédito, débito, Nequi, Bancolombia
                </p>

                <button
                  onClick={handleVolver}
                  className="w-full border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-xl transition text-sm"
                >
                  ← Volver y modificar
                </button>
              </div>
            )}
          </div>

          {/* ── Panel derecho: info + historial ── */}
          <div className="lg:col-span-2 space-y-4">
            {/* Métodos aceptados */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-bold text-[#0D1B2A] mb-3">Métodos de pago aceptados</h3>
              <div className="space-y-2">
                {[
                  { icon: "💳", label: "Tarjeta crédito / débito" },
                  { icon: "🟢", label: "Nequi" },
                  { icon: "🔵", label: "Bancolombia (PSE)" },
                  { icon: "🟡", label: "Daviplata" },
                  { icon: "🏦", label: "Efecty" },
                ].map(m => (
                  <div key={m.label} className="flex items-center gap-2.5 text-sm text-gray-700">
                    <span className="text-lg w-6 flex-shrink-0">{m.icon}</span>
                    {m.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Conceptos de pago */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-bold text-[#0D1B2A] mb-3">Conceptos disponibles</h3>
              <div className="space-y-1.5">
                {conceptos.map(c => (
                  <div
                    key={c.key}
                    onClick={() => { setConcepto(c.key); setStep("form"); }}
                    className="flex justify-between items-center text-xs cursor-pointer hover:bg-blue-50 rounded-lg px-2 py-1.5 transition group"
                  >
                    <span className="text-gray-700 group-hover:text-blue-700">{c.label}</span>
                    {c.precio_sugerido > 0 && (
                      <span className="text-gray-400 font-mono">{COP(c.precio_sugerido)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Historial de pagos ── */}
        <div className="mt-8 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-[#0D1B2A] flex items-center gap-2">
              <Clock size={17} className="text-blue-600" />
              Mis pagos recientes
            </h2>
            <button
              onClick={cargarHistorial}
              disabled={loadingHistorial}
              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw size={12} className={loadingHistorial ? "animate-spin" : ""} />
              Actualizar
            </button>
          </div>

          {historial.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <CreditCard size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin pagos registrados todavía</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Concepto</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Detalle</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Valor</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Estado</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Fecha</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map(p => {
                    const badge = ESTADO_BADGE[p.estado] ?? ESTADO_BADGE["PENDING"];
                    const fecha = p.fecha
                      ? new Date(p.fecha).toLocaleDateString("es-CO", {
                          day: "2-digit", month: "short", year: "numeric",
                        })
                      : "—";
                    return (
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                        <td className="py-2.5 px-3 font-medium text-[#0D1B2A]">{p.concepto_label}</td>
                        <td className="py-2.5 px-3 text-gray-500 max-w-[140px] truncate">
                          {p.concepto_detalle || "—"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-semibold text-[#0D1B2A]">
                          {COP(p.valor)}
                        </td>
                        <td className="py-2.5 px-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}
                          >
                            {badge.icon}
                            {badge.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-gray-500 text-xs">{fecha}</td>
                        <td className="py-2.5 px-3">
                          <code className="text-xs text-gray-400 font-mono">{p.referencia}</code>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
